// modules/person-events/shared/validators/person-event.validator.ts
import { z } from 'zod';
import { personEventTypeRegistry } from '../utils';

// Base schema that all events share
const personEventBaseSchema = z.object({
    personId: z.uuid('Invalid person ID format'),
    metadata: z.record(z.string(), z.unknown()),// Will be validated by specific event type
});

// Generic create schema that validates based on type field
export const createPersonEventInputSchema = z.object({
    type: z.string().min(1, 'Event type is required'),
    personId: z.uuid('Invalid person ID format'),
    metadata: z.record(z.string(), z.unknown()),
}).refine(
    (data) => {
        const result = personEventTypeRegistry.validate(data.type, data);
        return result.success;
    },
    {
        message: 'Event data does not match event type schema',
        path: ['metadata'],
    }
);

export type CreatePersonEventInput = z.infer<typeof createPersonEventInputSchema>;

/** Checkout-only payload; appointment-scoped checkout now goes through `appointment.checkOutVisitor(s)`. */
export const createCheckoutPersonEventInputSchema = createPersonEventInputSchema.refine(
  (data) => data.type === 'checkout',
  { message: 'Event type must be checkout', path: ['type'] },
);

export const createMultipleCheckoutPersonEventsInputSchema = z
  .array(createCheckoutPersonEventInputSchema)
  .min(1, 'At least one checkout event is required');

// Helper function to validate event with type safety
export function validatePersonEvent<T = unknown>(
    type: string,
    data: unknown
): { success: boolean; data?: T; error?: z.ZodError } {
    return personEventTypeRegistry.validate<T>(type, data);
}

// List schema
export const listPersonEventsSchema = z.object({
    personId: z.uuid('Invalid person ID format').optional(),
    type: z.string().optional(),
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(50),
    sortBy: z.enum(['createdAt']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type ListPersonEventsInput = z.infer<typeof listPersonEventsSchema>;